import styled from '@emotion/styled';
import { Button, Popover, Table } from 'antd';
import { default as CalenderIcon } from 'assets/svg/Calender';
import Cross from 'assets/svg/Cross';
import CrossAlt from 'assets/svg/CrossAlt';
import PlusSvgComponent from 'assets/svg/Plus';
import Barchart from 'components/molecules/Barchart';
import StackedBarChart from 'components/molecules/StackedBarChart';
import Calender, { DateRangeType } from 'components/molecules/Calender';
import { yAxisLabelClass } from 'components/template/GrowthAnalysis/ExpandedView';
import { colorConstants } from 'config/colorConstants';
import React, { FC, useContext, useEffect, useMemo, useRef, useState } from 'react';
import tw from 'twin.macro';
import { getComparisonColumns } from 'utils/getBumpReportColumns';
import { bumpReportComparisonTableData } from 'utils/testData';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import {
    calculateContainerHeight,
    calculateFontSize,
    fetchCurrency,
    formatNumWithKMB,
    formatNumWithKMBAlt,
    scaleMaxValue,
} from 'utils/helperMethods';
import Loader from 'components/molecules/Loader';
import ErrorBox from 'components/molecules/ErrorBox/ErrorBox';
import { calculateChartWidth } from './BumpChartContainer';
import _ from 'lodash';
import { FilterContextData } from 'contexts/FilterContext';
import { UserContext } from 'contexts/ConfigContext';
import { trackEventMixpanel } from 'utils/mixpanel';
import {
    BUMP_REPORT_PC_ADD_GROUP_A_CLICKED,
    BUMP_REPORT_PC_ADD_GROUP_A_EDIT_CLICKED,
    BUMP_REPORT_PC_ADD_GROUP_B_CLICKED,
    BUMP_REPORT_PC_ADD_GROUP_B_EDIT_CLICKED,
} from 'utils/mixpanelEventNames';
import moment from 'moment';
dayjs.extend(utc);
const addGroupHeader = 'Add Group';

const yAxisWidth = calculateFontSize(window.innerWidth, 60);

const containerHeight = calculateContainerHeight(3);

const containerClassName =
    'w-[50%] border flex justify-center items-center rounded h-[335px] bg-lightPurpleBg  border-borderGrey border-dashed cursor-pointer';
const labelClassName = 'text-promoColour flex justify-center items-center gap-2 font-lg';

const CustomButton = styled(Button)`
    &.ant-btn[disabled] {
        ${tw`bg-borderGrey text-promoColour rounded font-bold`}
    }
    &.ant-btn:focus:not(:disabled),
    &.ant-btn:hover:not(:disabled) {
        ${tw`border-purpleFont text-white opacity-80 bg-purpleFont`}
    }
    ${tw`min-h-[40px] p-3 leading-[normal] h-auto rounded bg-purpleFont text-white font-bold`}
`;

const ComparisonTable = styled(Table)`
    .ant-table-cell-row-hover {
        background: auto !important;
    }
    .ant-table-thead > tr > th {
        background: ${colorConstants.grey};
        padding: 0px;
        height: 44px;
        border-right: none !important;
    }
    .ant-table-header col:not(:last-child),
    .ant-table-body col:not(:last-child) {
        min-width: 100px;
    }
    .ant-table-thead
        > tr
        > th:not(:last-child):not(.ant-table-selection-column):not(.ant-table-row-expand-icon-cell):not(
            [colspan]
        ):before {
        background: none;
    }
    .ant-table-column-sorter {
        position: relative;
        left: -10px;
    }
    .ant-table-column-sorter-down.active,
    .ant-table-column-sorter-up.active {
        ${tw`text-purpleFont`};
    }
    border-radius: 4px;
    ${tw`border border-borderLinePurpuleAlt`}
`;

const incrementalComparisonTableData = bumpReportComparisonTableData('incremental');
const promoComparisonTableData = bumpReportComparisonTableData('promo');

const buttonFont = calculateFontSize(window.innerWidth, 13);

const tabComparisonMapping: any = {
    increments: { data: incrementalComparisonTableData },
    promo: { data: promoComparisonTableData },
    groupBy: { data: incrementalComparisonTableData },
};

type DefaultStateType = {
    step: number;
    dateRange: DateRangeType;
};

const defaultState: DefaultStateType = {
    step: 0,
    dateRange: [null, null],
};

const chartHeight = calculateFontSize(window.innerWidth, 300);

interface Props {
    createGroupClickHandler: (index: number, dateRange: any[], isSingleSelect: boolean) => void;
    onCloseComparison: (index: number) => void;
    chartData: any;
    chartConfig: any;
    dropDownValues: any;
    showAllLabels: boolean;
}

//MAIN COMPONENT STARTS HERE
const PromoComparisonContainer: FC<Props> = ({
    dropDownValues,
    createGroupClickHandler,
    onCloseComparison,
    chartData,
    chartConfig,
    showAllLabels,
}) => {
    const [groupA, setGroupA] = useState({ ...defaultState });
    const [groupB, setGroupB] = useState({ ...defaultState });
    //group items
    const items = useMemo(() => {
        const groupAState: DefaultStateType = groupA;
        const groupBState: DefaultStateType = groupB;
        return [
            {
                id: 1,
                state: groupAState,
                setState: setGroupA,
                label: `${addGroupHeader} A`,
                step1Label: 'Group A',
            },
            {
                id: 2,
                state: groupBState,
                setState: setGroupB,
                label: `${addGroupHeader} B`,
                step1Label: 'Group B',
            },
        ];
    }, [groupA, groupB]);

    //called when clicked on group item
    const containerClickHandler = (from: any, setFunction: Function) => {
        if (from.step) return;
        setFunction({ ...from, step: 1 });
    };

    return (
        <section className={` bg-white  flex justify-center gap-4`}>
            {items.map((item, index: number) => {
                const { id, state, label, setState } = item;
                return (
                    <div
                        key={id}
                        style={{ height: `max(335px, ${containerHeight}px)` }}
                        onClick={() => {
                            containerClickHandler(state, setState);
                            if (state.step === 0)
                                //mixpanel event
                                trackEventMixpanel(
                                    index === 0
                                        ? BUMP_REPORT_PC_ADD_GROUP_A_CLICKED
                                        : BUMP_REPORT_PC_ADD_GROUP_B_CLICKED,
                                );
                        }}
                        className={`${containerClassName} ${
                            state.step > 0
                                ? `!block border !border-borderLinePurpuleAlt !border-solid p-0 !bg-white !cursor-default ${
                                      state.step === 2 ? '!h-auto' : ''
                                  }`
                                : 'p-4'
                        }`}
                    >
                        {state.step > 0 ? (
                            <CreateGroupContainer
                                showAllLabels={showAllLabels}
                                index={index}
                                dropDownValues={dropDownValues}
                                createGroupClickHandler={createGroupClickHandler}
                                chartParams={{ chartConfig, chartData }}
                                item={item}
                                onCloseComparison={onCloseComparison}
                            />
                        ) : (
                            <DefaultContainer label={label} />
                        )}
                    </div>
                );
            })}
        </section>
    );
};

export default React.memo(PromoComparisonContainer);

interface CreateGroupProps {
    createGroupClickHandler: (index: number, dateRange: any[], isSingleSelect: boolean) => void;
    onCloseComparison: (index: number) => void;
    index: number;
    item: {
        state: DefaultStateType;
        setState: any;
        id: number;
        step1Label: string;
        label: string;
    };
    dropDownValues: any;
    chartParams: any;
    showAllLabels: boolean;
}

//STEP 1 AND STEP 2 CONTAINER
const CreateGroupContainer = ({
    dropDownValues,
    index,
    createGroupClickHandler,
    onCloseComparison,
    item,
    chartParams,
    showAllLabels,
}: CreateGroupProps) => {
    const { state, setState, id } = item;
    const [isOpen, setIsOpen] = useState(false);
    const [isSingleSelect, setIsSingleSelect] = useState(false);
    const [tempRange, setTempRange] = useState<DateRangeType>([...state.dateRange]);
    const {
        chartConfig,
        chartData: { axisValues, chartData },
    } = chartParams;
    const { appConfig: config } = useContext(UserContext);
    const { bumpReport, commonConfig } = config;
    const decimalForPercentage = commonConfig?.decimalForPercentage ?? 2;
    const decimalForNonPercentage = commonConfig?.decimalForNonPercentage ?? 2;
    const tooltipKeyLabelMapping = bumpReport.tooltipKeyLabelMapping;
    const tooltipExtraItems = bumpReport?.tooltipExtraItems;
    const groupByKey = bumpReport?.groupByKey;
    const { idForRestOfComponent, latestDate, calenderStartDay } = useContext(FilterContextData);
    const formattedLatestData = dayjs(latestDate, commonConfig?.dateFormat).format('MM/DD/YYYY');
    const latestYear = dayjs(latestDate, commonConfig?.dateFormat).year();
    const { tileData, color, selectedBumpReportTile1Data, selectedBumpReportTile2Data } = chartData;
    const data = chartData.data[index];
    const loading = chartData.loading[index];
    const error = chartData.error[index];
    const tableData = chartData.tableData[index];
    const graphRef = useRef<any>(null);
    const tableParams = useMemo(() => {
        if (tableData) {
            const memoizedColumns: any = getComparisonColumns(
                tableData,
                tileData?.volTile,
                decimalForNonPercentage,
                decimalForPercentage,
            );
            return {
                memoizedColumns,
                data: tableData,
            };
        }
        return {
            memoizedColumns: [],
            data: null,
        };
    }, [tableData, tileData?.volTile]);

    const disabledCondition = isSingleSelect ? !Boolean(state.dateRange?.[1]) : state.dateRange?.includes(null);

    //reset dates on tile change
    useEffect(() => {
        //call api again on tile change
        if (state.step === 2 && !disabledCondition) {
            onCreateGroupClick(state.dateRange);
        }
    }, [
        tileData,
        selectedBumpReportTile1Data,
        selectedBumpReportTile2Data,
        idForRestOfComponent,
        dropDownValues?.attributeTypeSelected,
        dropDownValues?.groupByOptionSelected,
    ]);

    const dateLabel = useMemo(() => {
        let startLabel = 'Start Date';
        let endLabel = 'End Date';
        const { dateRange } = state;
        if (dateRange[0]) startLabel = dayjs(new Date(dateRange[0])).format(commonConfig?.dateFormat);
        if (dateRange[1]) endLabel = dayjs(new Date(dateRange[1])).format(commonConfig?.dateFormat);
        return { startLabel, endLabel };
    }, [state]);

    const handleDateChange = (range: DateRangeType) => {
        setTempRange([...range]);
    };

    const handleOpenChange = (
        newOpen: boolean,
        isClickOutside: boolean = true,
        from: 'header' | 'dateRange' = 'dateRange',
    ) => {
        setIsOpen(newOpen);
        if (!newOpen && !isClickOutside) {
            //only save the date when clicked on set date button
            setState((prev: any) => ({ ...prev, dateRange: tempRange }));
            if (from === 'header') {
                onCreateGroupClick(tempRange);
            }
        } else if (!newOpen) setTempRange([...state.dateRange]); //if clicked outside dont save the change
    };

    const onCreateGroupClick = (dateRange: any[] = state.dateRange) => {
        setState((prev: any) => ({ ...prev, step: 2 }));
        const utcOffsetStartDate = dayjs(dateRange[0]).utcOffset();
        const utcOffsetEndDate = dayjs(dateRange[1]).utcOffset();
        const startDateUnix = dayjs(dateRange[0])
            .add(utcOffsetStartDate, 'm')
            .utc()
            .startOf('day')
            .subtract(6, 'day')
            .unix();
        const endDateUnix = dayjs(dateRange[1])
            .add(utcOffsetEndDate, 'm')
            .utc()
            .startOf('day')
            .subtract(6, 'day')
            .unix();
        createGroupClickHandler(index, [startDateUnix, endDateUnix], isSingleSelect);
    };

    const chartWidthConfig = useMemo(() => {
        if (data) {
            //since stack chart data will contain multiple data points for same weeks, hence getting uniq dates to get number of weeks
            const dateCount = _.uniqBy(data?.percentage?.data, 'date');
            const { updatedBarWidth, updatedWidth } = calculateChartWidth(
                graphRef.current?.offsetWidth - 65 || 600,
                13,
                dateCount.length,
            );
            return {
                barWidth: updatedBarWidth,
                width: updatedWidth,
            };
        }
        return {
            barWidth: chartConfig.barWidth,
            width: chartConfig.width,
        };
    }, [data]);
    const tileChartData = data ? data[tileData.absTile.key] : null;
    const currency = tileData?.volTile?.key === 'val' && tileData?.absTile.key === 'abs' ? fetchCurrency(config) : '';

    // console.log(chartConfig, graphRef.current?.offsetWidth, updatedBarWidth, updatedWidth, 'logging width');
    return (
        <div ref={graphRef} className="h-full w-full">
            <div className="p-4 bg-bgBackground text-purpleFont font-sm flex justify-between items-center min-h-[50px] font-bold">
                {state.step === 1 ? (
                    item.step1Label
                ) : (
                    <>
                        <Popover
                            trigger="click"
                            open={isOpen}
                            onOpenChange={handleOpenChange}
                            placement={'right'}
                            showArrow={false}
                            //overlayInnerStyle={{ position: 'relative', right: 20, padding: 0 }}
                            content={
                                <Calender
                                    showSingleSelectOption={true}
                                    singleSelectOptionChange={(val: boolean) => {
                                        setState({ ...state, dateRange: [null, null] }); //reset date when single select is changed
                                        setTempRange([null, null]);
                                        setIsSingleSelect(val);
                                    }}
                                    singleDateSelect={isSingleSelect}
                                    startYear={latestYear === 1970 || !latestYear ? undefined : latestYear}
                                    disabledDates={[null, formattedLatestData]}
                                    startDay={calenderStartDay}
                                    range={tempRange}
                                    onChange={handleDateChange}
                                    onFooterClick={() => handleOpenChange(false, false, 'header')}
                                />
                            }
                        >
                            <span
                                onClick={() => {
                                    //mixpanel event
                                    trackEventMixpanel(
                                        index === 0
                                            ? BUMP_REPORT_PC_ADD_GROUP_A_EDIT_CLICKED
                                            : BUMP_REPORT_PC_ADD_GROUP_B_EDIT_CLICKED,
                                    );
                                }}
                                className="underline cursor-pointer min-w-[200px]"
                            >
                                {isSingleSelect
                                    ? `${dateLabel.endLabel}`
                                    : `${dateLabel.startLabel} - ${dateLabel.endLabel}`}
                            </span>
                        </Popover>
                        <span
                            onClick={() => {
                                setState({ ...defaultState });
                                onCloseComparison(index);
                            }}
                            className="text-[30px] cursor-pointer"
                        >
                            <CrossAlt />
                        </span>
                    </>
                )}
            </div>
            {state.step == 1 ? (
                <div style={{ height: 'calc(100% - 50px)' }}>
                    <Popover
                        trigger="click"
                        open={isOpen}
                        onOpenChange={handleOpenChange}
                        placement={'top'}
                        showArrow={false}
                        // overlayInnerStyle={{ padding: 0 }}
                        content={
                            <Calender
                                showSingleSelectOption={true}
                                singleSelectOptionChange={(val: boolean) => {
                                    setState({ ...state, dateRange: [null, null] }); //reset date when single select is changed
                                    setTempRange([null, null]);
                                    setIsSingleSelect(val);
                                }}
                                singleDateSelect={isSingleSelect}
                                startYear={latestYear === 1970 || !latestYear ? undefined : latestYear}
                                disabledDates={[null, formattedLatestData]}
                                startDay={calenderStartDay}
                                range={tempRange}
                                onChange={handleDateChange}
                                onFooterClick={() => handleOpenChange(false, false)}
                            />
                        }
                    >
                        <div className="flex justify-center items-center relative top-[50%] translate-y-[-50%]">
                            <div className="cursor-pointer min-w-[233px] min-h-[40px] flex justify-start gap-3 items-center p-3 border border-purpleFont rounded mr-4">
                                <CalenderIcon />
                                <div className="flex w-full justify-center">
                                    {!isSingleSelect && (
                                        <>
                                            <span
                                                className={`${
                                                    !state.dateRange.includes(null) && !isOpen
                                                        ? 'text-subHeaderColor'
                                                        : 'text-borderGrey'
                                                }  font-xs`}
                                            >
                                                {dateLabel.startLabel}
                                            </span>
                                            <span
                                                className={`${
                                                    !state.dateRange.includes(null) && !isOpen
                                                        ? 'text-subHeaderColor'
                                                        : 'text-borderGrey'
                                                } mx-2  font-xs`}
                                            >
                                                -
                                            </span>
                                        </>
                                    )}

                                    <span
                                        className={`${
                                            !disabledCondition && !isOpen ? 'text-subHeaderColor' : 'text-borderGrey'
                                        }  font-xs`}
                                    >
                                        {dateLabel.endLabel}
                                    </span>
                                </div>
                            </div>

                            <CustomButton
                                onClick={(e) => {
                                    e.stopPropagation();
                                    onCreateGroupClick(tempRange);
                                }}
                                className="font-xs"
                                style={{ fontSize: buttonFont }}
                                disabled={disabledCondition}
                            >
                                Create Group
                            </CustomButton>
                        </div>
                    </Popover>
                </div>
            ) : (
                <>
                    {
                        // IF ERROR RETURN ERROR MESSAGE
                        error && !loading && !data ? (
                            <div className="p-4 h-[calc(100%-50px)]">
                                <ErrorBox style={{ justifyContent: 'center', height: '100%' }} msg={String(error)} />
                            </div>
                        ) : loading ? (
                            <div
                                className="p-4 h-[calc(100%-50px)]"
                                style={{ height: 285, display: 'flex', justifyContent: 'center', alignItems: 'center' }}
                            >
                                <Loader style={{ height: '100%' }} />
                            </div>
                        ) : (
                            <div className="p-4">
                                <p className={`${yAxisLabelClass} !top-0 mb-2`}>{axisValues?.y1AxisLabel}</p>
                                <div className="flex ">
                                    {/* Y1 AXIS */}
                                    <Barchart
                                        // padding={[0, 0, 0, 48]}
                                        padding={[0, 0, 17, 0]}
                                        barWidth={chartWidthConfig?.barWidth}
                                        xAxisConfig={false}
                                        dataset={[]}
                                        height={chartHeight}
                                        loading={false}
                                        chartContainer={`bumpChart-y1-comp-${id}`}
                                        fields={['date', 'sales']}
                                        minBound={tileChartData?.minVal}
                                        maxBound={
                                            tileData?.absTile?.key === 'percentage'
                                                ? tileChartData?.maxVal
                                                : scaleMaxValue(40, tileChartData?.maxVal)
                                        }
                                        width={yAxisWidth}
                                        colorKey={'type'}
                                        yAxisLabelFormatter={(val: any) =>
                                            tileData.absTile.key === 'percentage'
                                                ? formatNumWithKMBAlt(val, decimalForPercentage) + '%'
                                                : formatNumWithKMBAlt(
                                                      val,
                                                      decimalForNonPercentage,
                                                      decimalForNonPercentage,
                                                  )
                                        }
                                        // Yline
                                        YGrid={false}
                                    />
                                    {/* CHART COMPONENT */}
                                    <div className=" overflow-x-auto w-full pb-[40px]">
                                        <StackedBarChart
                                            barWidth={chartWidthConfig?.barWidth}
                                            currency={currency}
                                            totalKey={tooltipKeyLabelMapping[tileData.bumpReportTile.key]}
                                            // yAxisConfig={false}
                                            tooltipFormatter={(xAxis: any, val: any) => {
                                                return {
                                                    name: xAxis,
                                                    value:
                                                        tileData.absTile.key === 'percentage'
                                                            ? formatNumWithKMBAlt(val, decimalForPercentage) + '%'
                                                            : currency +
                                                              formatNumWithKMBAlt(
                                                                  val,
                                                                  decimalForNonPercentage,
                                                                  decimalForNonPercentage,
                                                              ),
                                                };
                                            }}
                                            intervalFormatter={(val) => {
                                                return tileData.absTile.key === 'percentage'
                                                    ? formatNumWithKMBAlt(val, decimalForPercentage) + '%'
                                                    : currency +
                                                          formatNumWithKMBAlt(
                                                              val,
                                                              decimalForNonPercentage,
                                                              decimalForNonPercentage,
                                                          );
                                            }}
                                            dataset={tileChartData?.data || []}
                                            height={chartHeight}
                                            //  showLegend
                                            loading={false}
                                            chartContainer={`bumpChart-main-comp-${id}`}
                                            fields={['date', 'sales', 'type', 'value', 'index', 'areaData']}
                                            minBound={tileChartData?.minVal}
                                            maxBound={
                                                tileData?.absTile?.key === 'percentage'
                                                    ? tileChartData?.maxVal
                                                    : scaleMaxValue(40, tileChartData?.maxVal)
                                            }
                                            areaChartParams={
                                                tileData?.absTile?.key === 'percentage' ||
                                                tileData.bumpReportTile.key === groupByKey
                                                    ? undefined
                                                    : {
                                                          tooltipLabel: 'YA Sales',

                                                          minBound: tileChartData?.minVal,
                                                          maxBound:
                                                              tileData?.absTile?.key === 'percentage'
                                                                  ? tileChartData?.maxVal
                                                                  : scaleMaxValue(40, tileChartData?.maxVal),
                                                      }
                                            }
                                            width={chartWidthConfig?.width || window.innerWidth}
                                            xAxisLabelFormatter={(val: any, item: any, index: number) => {
                                                if (index % 4 === 0) return val;
                                            }}
                                            extras={{ data: tileChartData?.extras, dataMapping: tooltipExtraItems }}
                                            Xline={false}
                                            skipIntervalLabel={showAllLabels ? 1 : 4}
                                            colorKey={'type'}
                                            intervalLabelKey="totalSales"
                                            groupedChartColor={color}
                                        />
                                    </div>
                                </div>
                                <p className={`${yAxisLabelClass} !top-[-36px] relative text-center`}>
                                    {axisValues?.xAxisLabel}
                                </p>
                                {/* table component */}
                                <div className="mt-2 overflow-y-auto max-h-[200px]">
                                    <ComparisonTable
                                        pagination={false}
                                        scroll={{ x: '100%', y: 800 }}
                                        sticky
                                        rowKey={(record: any) => record['PPG']}
                                        columns={tableParams.memoizedColumns}
                                        dataSource={tableParams.data}
                                    />
                                </div>
                            </div>
                        )
                    }
                </>
            )}
        </div>
    );
};

//default comparison component
const DefaultContainer = ({ label }: { label: string }) => {
    return (
        <p className={`${labelClassName}`}>
            <PlusSvgComponent fill={colorConstants.darkGrey} />
            {label}
        </p>
    );
};
